import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card, Table, Button, Tag, Progress, Typography, Space, Popconfirm,
  message, Statistic, Row, Col,
} from 'antd';
import {
  ThunderboltOutlined, EyeOutlined, ReloadOutlined, DeleteOutlined,
  FolderAddOutlined, EyeInvisibleOutlined,
} from '@ant-design/icons';
import axios from 'axios';

const { Title, Text } = Typography;

interface AutoModSession {
  id: number;
  name: string;
  dataset_id: number | null;
  status: string;
  total_images: number;
  classified_count: number;
  routed_count: number;
  excluded_count: number;
  model3_used_count: number;
  failed_count: number;
  created_at: string;
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3003/api';

const AutoModerationList: React.FC = () => {
  const [sessions, setSessions] = useState<AutoModSession[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    fetchSessions();
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, []);

  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (sessions.some(s => s.status === 'running')) {
      intervalRef.current = setInterval(fetchSessions, 5000);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [sessions]);

  const fetchSessions = async () => {
    try {
      const res = await axios.get(`${API_URL}/auto-moderation-sessions`);
      setSessions(res.data);
    } catch (error) {
      message.error('Failed to load auto-moderation sessions');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await axios.delete(`${API_URL}/auto-moderation-sessions/${id}`);
      message.success('Session deleted');
      fetchSessions();
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to delete session');
    }
  };

  const statusTag = (status: string) => {
    const c: Record<string, string> = {
      pending: 'default', running: 'processing', paused: 'warning',
      completed: 'success', failed: 'error',
    };
    return <Tag color={c[status] || 'default'}>{status.toUpperCase()}</Tag>;
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, r: AutoModSession) => (
        <a onClick={() => navigate(`/auto-moderation/${r.id}`)}>{name}</a>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (s: string) => statusTag(s),
    },
    {
      title: 'Progress',
      key: 'progress',
      render: (_: any, r: AutoModSession) => {
        const total = r.total_images || 1;
        const processed = r.classified_count + r.failed_count;
        const percent = Math.round((processed / total) * 100);
        return (
          <div style={{ minWidth: 180 }}>
            <Progress percent={percent} size="small"
              status={r.status === 'completed' ? 'success' : r.status === 'failed' ? 'exception' : 'active'} />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {r.classified_count.toLocaleString()} / {total.toLocaleString()}
            </Text>
          </div>
        );
      },
    },
    {
      title: 'Routed',
      dataIndex: 'routed_count',
      key: 'routed_count',
      render: (n: number) => (
        <Text style={{ color: '#1890ff' }}><FolderAddOutlined /> {n.toLocaleString()}</Text>
      ),
    },
    {
      title: 'Censored',
      dataIndex: 'excluded_count',
      key: 'excluded_count',
      render: (n: number) => (
        <Text style={{ color: '#faad14' }}><EyeInvisibleOutlined /> {n.toLocaleString()}</Text>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (d: string) => new Date(d).toLocaleString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, r: AutoModSession) => (
        <Space>
          <Button icon={<EyeOutlined />} size="small" onClick={() => navigate(`/auto-moderation/${r.id}`)}>
            View
          </Button>
          <Popconfirm title="Delete this session?" onConfirm={() => handleDelete(r.id)}
            okText="Delete" cancelText="Cancel">
            <Button danger icon={<DeleteOutlined />} size="small" disabled={r.status === 'running'} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const totals = sessions.reduce(
    (a, s) => ({
      running: a.running + (s.status === 'running' ? 1 : 0),
      routed: a.routed + s.routed_count,
      excluded: a.excluded + s.excluded_count,
    }),
    { running: 0, routed: 0, excluded: 0 }
  );

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <ThunderboltOutlined style={{ fontSize: 28, color: '#6366f1' }} />
            <Title level={2} style={{ margin: 0 }}>Auto-Moderation</Title>
          </div>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={fetchSessions}>Refresh</Button>
            <Button type="primary" icon={<ThunderboltOutlined />} onClick={() => navigate('/moderation/create')}>
              New (from filter)
            </Button>
          </Space>
        </div>

        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={6}><Statistic title="Sessions" value={sessions.length} /></Col>
          <Col span={6}><Statistic title="Running" value={totals.running} valueStyle={{ color: '#1890ff' }} /></Col>
          <Col span={6}><Statistic title="Total Routed" value={totals.routed} valueStyle={{ color: '#52c41a' }} /></Col>
          <Col span={6}><Statistic title="Total Censored" value={totals.excluded} valueStyle={{ color: '#faad14' }} /></Col>
        </Row>

        <Table columns={columns} dataSource={sessions} rowKey="id" loading={loading}
          pagination={{ pageSize: 10 }} />
      </Card>
    </div>
  );
};

export default AutoModerationList;
